function createSignal(initialState = null) {
	function subscribe(handler) {
		const self = this
		this.subscribers.add(handler)

		return function () {
			self.subscribers.delete(handler)
		}
	}

	function update(state) {
		this.state = state

		for (const handler of this.subscribers) {
			handler(state)
		}
	}

	return {
		state: initialState,
		subscribers: new Set(),
		update,
		subscribe,
	}
}

const pointerSignal = createSignal()

if (window.matchMedia('(hover: none)').matches) {
	window.addEventListener('scroll', () => {
		pointerSignal.update({
			viewportX: window.innerWidth / 2,
			viewportY: window.innerHeight / 2,
		})
	})
} else {
	window.addEventListener('pointermove', e => {
		pointerSignal.update({
			viewportX: e.clientX,
			viewportY: e.clientY,
		})
	})
}

const members = document.querySelectorAll('.member-photo')

members.forEach(async (member, index) => {
	let rectX = 1
	let rectY = 1
	let rectWidth = 1
	let rectHeight = 1

	const body = member.querySelector('.main')
	const pupils = member.querySelector('.pupils')
	const lids = member.querySelector('.lids')

	let left = {
		x: parseFloat(pupils.dataset.leftx),
		y: parseFloat(pupils.dataset.lefty),
	}
	let right = {
		x: parseFloat(pupils.dataset.rightx),
		y: parseFloat(pupils.dataset.righty),
	}

	const updateMemberRect = () => {
		const rect = member.getBoundingClientRect()
		rectX = rect.x || 1
		rectY = rect.y || 1
		rectWidth = rect.width || 1
		rectHeight = rect.height || 1
	}

	// Setup initial rects
	updateMemberRect()

	// Scroll updates
	window.addEventListener(
		'scroll',
		() => {
			updateMemberRect()
		},
		{ passive: true }
	)

	// Resize updates
	window.addEventListener(
		'resize',
		debounce(() => {
			updateMemberRect()
			eyeTracking(member, left, right)
		}, 100)
	)

	// Observe member size changes
	const resizeObserver = new ResizeObserver(() => {
		updateMemberRect()
		eyeTracking(member, left, right)
	})
	resizeObserver.observe(member)

	// Wait for all images to load before tracking eyes
	await waitForAllImages([body, pupils, lids])
	eyeTracking(member, left, right)

	// Pointer position subscription
	pointerSignal.subscribe(pointerPosition => {
		const x = pointerPosition.viewportX - rectX
		const y = pointerPosition.viewportY - rectY

		const normalX = (x / rectWidth) * 2 - 1
		const normalY = (y / rectHeight) * 2 - 1
		const eyeCenter = averagePosition(left, right)

		const newPointerPosition = {
			x: changeScale(normalX, -1, 1, 0, 1),
			y: changeScale(normalY, -1, 1, 0, 1),
		}

		const pointerPositionRelToEyes = {
			x: newPointerPosition.x - eyeCenter.x,
			y: newPointerPosition.y - eyeCenter.y,
		}

		const eyePointerPosition = {
			x: clamp(pointerPositionRelToEyes.x, -0.6, 0.6),
			y: clamp(pointerPositionRelToEyes.y, -0.3, 0.3),
		}

		pupils.style.transform = `translate(${eyePointerPosition.x}%, ${eyePointerPosition.y}%)`
	})
})

// ========== Utility Functions ==========

function waitForImage(image) {
	if (image.complete) {
		return Promise.resolve(image)
	}
	return new Promise((resolve, reject) => {
		image.onload = () => resolve(image)
		image.onerror = () => reject(new Error('image load error'))
	})
}

function waitForAllImages(images) {
	const promises = images.map(waitForImage)
	return Promise.all(promises)
}

function eyeTracking(member, left, right) {
	const rect = member.getBoundingClientRect()
	const holderAspectRatio = roundTo(rect.width / rect.height)
	const main = member.querySelector('.main')

	const imageAspectRatio =
		main?.naturalWidth && main?.naturalHeight
			? main.naturalWidth / main.naturalHeight
			: 1

	if (holderAspectRatio >= imageAspectRatio) {
		const imageHeightVisible = imageAspectRatio / holderAspectRatio
		left.x = parseFloat(member.querySelector('.pupils').dataset.leftx)
		left.y =
			parseFloat(member.querySelector('.pupils').dataset.lefty) /
			imageHeightVisible
		right.x = parseFloat(member.querySelector('.pupils').dataset.rightx)
		right.y =
			parseFloat(member.querySelector('.pupils').dataset.righty) /
			imageHeightVisible
		return
	}

	const imageWidthVisible = holderAspectRatio / imageAspectRatio

	const leftDistanceFromCenter =
		parseFloat(member.querySelector('.pupils').dataset.leftx) - 0.5
	const rightDistanceFromCenter =
		parseFloat(member.querySelector('.pupils').dataset.rightx) - 0.5

	left.x = leftDistanceFromCenter / imageWidthVisible + 0.5
	left.y = parseFloat(member.querySelector('.pupils').dataset.lefty)

	right.x = rightDistanceFromCenter / imageWidthVisible + 0.5
	right.y = parseFloat(member.querySelector('.pupils').dataset.righty)
}

function averagePosition(left, right) {
	return {
		x: (left.x + right.x) / 2,
		y: (left.y + right.y) / 2,
	}
}

function clamp(value, min, max) {
	return Math.max(Math.min(value, max), min)
}

function changeScale(value, minA, maxA, minB, maxB) {
	const rangeA = maxA - minA
	const rangeB = maxB - minB
	const normalized = (value - minA) / rangeA
	return normalized * rangeB + minB
}

function roundTo(n, decimalPlaces = 3) {
	const factor = Math.pow(10, decimalPlaces)
	return Math.round(n * factor) / factor
}

function debounce(fn, delay) {
	let timer = null
	return (...args) => {
		clearTimeout(timer)
		timer = setTimeout(() => fn(...args), delay)
	}
}
